import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    TextField,
    Typography
} from '@mui/material';
import { useFormik } from 'formik';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import AbstractContentSection from 'views/DashboardOperator/SubmittedAbstract/sections/AbstractContentSection';
import * as yup from 'yup';

const AbstractScoringSection = ({ data }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const validationSchema = yup.object().shape({
        score: yup
            .number()
            .typeError('Please provide a numeric score')
            .required('Please provide a score')
            .min(1, 'Minimum score is 1')
            .max(100, 'Maximum score is 100'),
    });

    const initialScore =
        data?.scorings?.find(
            (scoring) => scoring?.judgeId?.id === user?.id,
        )?.score ?? '';

    const initialValues = {
        score: initialScore,
    };

    const onSubmit = async (values) => {
        setLoading(true);
        const payload = {
            submissionId: data?.id,
            judgeId: user?.id,
            score: Number(values.score),
        };

        await axiosInstance
            .post('api/prv/judge-submissions/scoring', payload)
            .then(() => {
                handleNavigate();
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const handleNavigate = () => {
        if (initialScore !== '') {
            navigate('/dashboard/judge/abstract-scored');

            return;
        }

        navigate('/dashboard/judge/abstract-to-score');
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit,
    });

    return (
        <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
                Abstract Scoring
            </Typography>
            <Divider sx={{ my: 2 }} />

            {data && <AbstractContentSection data={data} />}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Scoring guideline</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        After reviewing the abstract, please proceed to assign a score based on the following variables:
                    </Typography>
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                        <li><span style={{ fontWeight: 'bold' }}>Novelty</span></li>
                        <li>
                            <span style={{ fontWeight: 'bold' }}>Case description&nbsp;</span> 
                            (for <span style={{ fontWeight: 400, fontStyle: 'italic' }}>Case Reports</span>) or&nbsp;
                            <span style={{ fontWeight: 'bold' }}>Methods&nbsp;</span> 
                            (for&nbsp;
                                <span style={{ fontWeight: 400, fontStyle: 'italic' }}>Original Research&nbsp;</span>
                            and&nbsp;
                                <span style={{ fontWeight: 400, fontStyle: 'italic' }}>Systematic Review / Meta-Analisys</span>)</li>
                        <li><span style={{ fontWeight: 'bold' }}>Writing</span></li>
                        <li><span style={{ fontWeight: 'bold' }}>Clinical relevance</span></li>
                    </ol>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        The abstract should be evaluated on a scale of 1 - 100 with the following considerations:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>A total score above 80:&nbsp;
                            <span style={{ fontWeight: 'bold' }}>eligible</span> for acceptance as an <span style={{ fontStyle: 'italic' }}>oral presentation</span></li>
                        <li>A score of 60 - 80: 
                            <span style={{ fontWeight: 'bold' }}>eligible</span> for acceptance as a <span style={{ fontStyle: 'italic' }}>displayed poster</span></li>
                        <li>A score below 60: <span style={{ fontWeight: 'bold' }}>not eligible</span> for acceptance</li>
                    </ul>
                </Alert>

                {formik.values.score && (
                    <Chip
                        label={
                            Number(formik.values.score) > 100
                                ? 'Invalid Score'
                                : Number(formik.values.score) > 80  
                                ? 'Oral Presentation'
                                : Number(formik.values.score) >= 60
                                ? 'Displayed Poster'
                                : 'Not Eligible'
                        }
                        color={
                            Number(formik.values.score) > 100
                                ? 'error'
                                : Number(formik.values.score) > 80
                                ? 'primary'
                                : Number(formik.values.score) >= 60
                                ? 'info'
                                : 'default'
                        }
                        variant="filled"
                        sx={{ height: 28, my: 2 }}
                    />
                )}

                <form onSubmit={formik.handleSubmit}>
                    <TextField
                        variant="outlined"
                        name="score"
                        type="number"
                        value={formik.values.score}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        inputProps={{ min: 1, max: 100 }}
                        error={
                            formik.touched.score &&
                            Boolean(formik.errors.score)
                        }
                        helperText={
                            (formik.touched.score &&
                                formik.errors.score) ||
                            'Enter an score between 1 and 100'
                        }
                    />
                    
                    <Button
                        size="medium"
                        variant="contained"
                        type="submit"
                        color="success"
                        disabled={loading}
                        sx={{ marginLeft: 2, marginTop: 0.75 }}
                    >
                        {loading ? (
                            <>
                                Saving Score&nbsp;&nbsp;
                                <CircularProgress size={18} />
                            </>
                        ) : (
                            'Save Score'
                        )}
                    </Button>
                </form>
            </Box>
        </Card>
    );
};

export default AbstractScoringSection;
